import { useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PlusIcon } from "@heroicons/react/20/solid";
import { cn } from "@utils";
import OverdueBurndownCard from "./OverdueBurndownCard";
import WorkingSetBoard from "./WorkingSetBoard";
import WorkingSetBuilderModal from "./WorkingSetBuilderModal";
import RemoveWorkingSetDialog from "./RemoveWorkingSetDialog";
import { useWorkingSetActions } from "../hooks/useWorkingSetActions";
import { useHomeLayout } from "../hooks/homeLayout";
import SuggestedForToday from "./SuggestedForToday";
import { useAiFeatures } from "@flags";

const EmptyWorkingSets = ({ onAdd }) => (
  <div className="flex flex-col items-start gap-4 rounded-2xl border border-dashed border-zinc-300 p-8 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex flex-col gap-1">
      <p className="text-sm font-semibold text-zinc-800">Pin the accounts you work every day</p>
      <p className="max-w-xl text-sm text-zinc-500">
        A working set is a list of accounts that keeps itself up to date, like everyone over their credit limit or more
        than 60 days late. Each one shows here with a live count, and opens straight into Trade accounts.
      </p>
    </div>
    <button
      type="button"
      onClick={onAdd}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-zinc-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-zinc-700"
    >
      <PlusIcon aria-hidden="true" className="size-4" />
      New working set
    </button>
  </div>
);

const GripIcon = () => (
  <svg viewBox="0 0 10 16" className="h-4 w-2.5" fill="currentColor" aria-hidden="true">
    {[2, 8, 14].flatMap((y) => [2, 8].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.4} />))}
  </svg>
);

// A Home section that can be dragged into a new place by the grip in the left gutter. Only the grip starts
// a drag, so the chart's hover and the working-set board's own drag-and-drop work as before.
const SortableSection = ({ id, label, children }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn("group/section relative", isDragging && "z-10 opacity-80")}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Move ${label}`}
        className={cn(
          "absolute -left-6 top-0 flex h-5 w-5 cursor-grab touch-none items-center justify-center rounded text-zinc-300",
          "opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-500 focus-visible:opacity-100 group-hover/section:opacity-100",
          isDragging && "cursor-grabbing opacity-100",
        )}
      >
        <GripIcon />
      </button>
      {children}
    </div>
  );
};

const SECTION_LABELS = {
  burndown: "overdue burndown",
  workingSets: "working sets",
  suggestions: "suggestions",
};

const HomeTab = ({ workingSets, workingSetsLoading }) => {
  // null, or { slot } — the board slot a new set lands in (null slot = first free).
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const { duplicate, place } = useWorkingSetActions();
  const aiEnabled = useAiFeatures();
  const { order, save } = useHomeLayout();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const visible = order.filter((id) => id !== "suggestions" || aiEnabled);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    save(arrayMove(order, order.indexOf(active.id), order.indexOf(over.id)));
  };

  const sections = {
    burndown: <OverdueBurndownCard workingSets={workingSets} />,
    workingSets: (
      <section className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Working sets</h3>
          {workingSets.length > 0 && (
            <span className="text-xs text-zinc-400">Live counts · opens in Trade accounts</span>
          )}
        </div>
        {!workingSetsLoading && workingSets.length === 0 ? (
          <EmptyWorkingSets onAdd={() => setCreating({ slot: null })} />
        ) : (
          <WorkingSetBoard
            sets={workingSets}
            onPlace={(set, slot) => place(set, slot, workingSets)}
            onAdd={(slot) => setCreating({ slot })}
            onEdit={setEditing}
            onDuplicate={(set) => duplicate(set, workingSets)}
            onRemove={setRemoving}
          />
        )}
      </section>
    ),
    suggestions: <SuggestedForToday />,
  };

  return (
    <div className="flex flex-col gap-10">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={visible} strategy={verticalListSortingStrategy}>
          {visible.map((id) => (
            <SortableSection key={id} id={id} label={SECTION_LABELS[id]}>
              {sections[id]}
            </SortableSection>
          ))}
        </SortableContext>
      </DndContext>

      <WorkingSetBuilderModal isOpen={!!creating} slot={creating?.slot ?? null} onClose={() => setCreating(null)} />
      <WorkingSetBuilderModal isOpen={!!editing} set={editing} onClose={() => setEditing(null)} />
      <RemoveWorkingSetDialog set={removing} onClose={() => setRemoving(null)} />
    </div>
  );
};

export default HomeTab;
